import { createStyles, Group, UnstyledButton } from '@mantine/core';
import { ListBox, Text } from '@repo/foundations';
import { isNil } from 'lodash-es';
import { useCallback } from 'react';
import { FilterDropdownList } from '../FilterDropdown/FilterDropdownList';
import type {
	FilterDropdownConfigList,
	FilterItem,
	FilterValue,
	FilterValueType,
} from '../types';
import { FilterType } from '../types';
import { useNestedFilterLabel } from '../useNestedFilterLabel';

export interface FilterNestedTargetMenuProps {
	value: FilterValue;
	onChange: (
		value: FilterValue,
		close: boolean,
		shouldCloseNested?: boolean
	) => void;
	onToggle: () => void;
	opened: boolean;
	config: FilterDropdownConfigList;
	closeNested?: () => void;
}

const useStyles = createStyles((theme) => ({
	target: {
		backgroundColor: theme.other.getColor('fill/primary/default'),
		padding: `${theme.spacing['5xs']} ${theme.spacing['2xs']}`,

		'&:hover, &:focus': {
			backgroundColor: theme.other.getColor('fill/primary/hover'),
		},

		'&:active': {
			backgroundColor: theme.other.getColor('fill/primary/active'),
		},
	},
	detailedValue: {
		fontSize: theme.fontSizes.xs,
		color: theme.other.getColor('text/primary/default'),
		lineHeight: theme.spacing.lg,
	},
}));

export function FilterNestedTargetMenu({
	value,
	onChange,
	onToggle,
	opened,
	config,
	closeNested,
}: FilterNestedTargetMenuProps) {
	const { classes } = useStyles();

	const handleOnSelect = useCallback(
		(item: FilterItem) => {
			if (!config.filterType || config.filterType === FilterType.Multiple) {
				onChange(
					{
						...value,
						subValue: [item.value],
					},
					true,
					true
				);
			} else if (config.filterType === FilterType.Single) {
				onChange(
					{
						...value,
						subValue: item.value,
					},
					true,
					true
				);
			} else {
				throw new Error('Invalid filter type');
			}
			closeNested?.();
		},
		[config.filterType, onChange, value, closeNested]
	);

	const handleOnDeselect = useCallback(
		(item: FilterItem) => {
			if (config.filterType === FilterType.Single) {
				// selecting a already checked radio button shouldn't do anything
				// NOOP
			} else if (
				!config.filterType ||
				config.filterType === FilterType.Multiple
			) {
				let currentValue: FilterValueType[] = [];

				if (!isNil(value.subValue)) {
					currentValue = Array.isArray(value.subValue)
						? value.subValue
						: [value.subValue];
				}

				const newValue = currentValue.filter(
					(currentValueItem) => currentValueItem !== item.value
				);

				onChange(
					{
						...value,
						subValue: newValue,
					},
					true,
					true
				);
			} else {
				throw new Error('Invalid filter type');
			}
			closeNested?.();
		},
		[config.filterType, onChange, value, closeNested]
	);

	const valueLabel = useNestedFilterLabel({
		getItems: config.getItems,
		getItemsById: config.getItemsById,
		value,
	});

	return (
		<ListBox opened={opened} onOpenChange={onToggle}>
			<ListBox.Target>
				<UnstyledButton onClick={onToggle} className={classes.target}>
					<Group spacing="2xs">
						<Text className={classes.detailedValue}>
							{valueLabel || '(no selection)'}
						</Text>
					</Group>
				</UnstyledButton>
			</ListBox.Target>
			<FilterDropdownList
				value={value}
				filterType={config.filterType}
				renderMenuItem={config.renderMenuItem}
				getItems={config.getItems}
				getItemsById={config.getItemsById}
				onChange={onChange}
				searchPlaceholder={config.searchPlaceholder}
				handleOnSelect={handleOnSelect}
				handleOnDeselect={handleOnDeselect}
				isNested={true}
			/>
		</ListBox>
	);
}
